import React, {
  ComponentType,
  LazyExoticComponent,
  ReactElement,
  createElement,
  forwardRef,
  lazy,
  useEffect,
  useRef,
} from 'react'

type LazyPreloadComponent<T extends ComponentType<any>> = LazyExoticComponent<T> & {
  preload?: () => void
}

export function lazyPreload(
  importStatement: () => Promise<{ default: ComponentType<any> }>,
): LazyPreloadComponent<ComponentType<any>> {
  const Component = React.lazy(importStatement)
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  Component.preload = importStatement
  return Component as LazyPreloadComponent<ComponentType<any>>
}

export type PreloadableComponent<T extends ComponentType<any>> = T & {
  preload: () => Promise<T>
}

export function lazyWithPreload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): PreloadableComponent<T> {
  const ReactLazyComponent = lazy(factory)
  let PreloadedComponent: T | undefined
  let factoryPromise: Promise<T> | undefined

  const Component = forwardRef((props, ref) => {
    // Once one of these is chosen, we must ensure that it continues to be
    // used for all subsequent renders, otherwise it can cause the
    // underlying component to be unmounted and remounted.
    const ComponentToRender = useRef(PreloadedComponent ?? ReactLazyComponent)
    return createElement(ComponentToRender.current, Object.assign(ref ? { ref } : {}, props) as any)
  })

  const LazyWithPreload = Component as any as PreloadableComponent<T>

  LazyWithPreload.preload = () => {
    if (!factoryPromise) {
      factoryPromise = factory().then((module) => {
        PreloadedComponent = module.default
        return PreloadedComponent
      })
    }

    return factoryPromise
  }

  return LazyWithPreload
}

interface PreloadWrapperProps<T extends ComponentType<any>> {
  component: ReactElement<T>
  componentsToPreload: any
}

export function PreloadWrapper<T extends ComponentType<any>>({ component, componentsToPreload }: any) {
  useEffect(() => {
    componentsToPreload.forEach((component: any) => {
      component.preload?.()
    })
  }, [componentsToPreload])

  return component
}
